/** Deal Engine card — v1's listing-panel score + suggested-offer block,
 * rendered in the lead drawer right under the ARV inputs so a typed Comped
 * ARV immediately reprices the MAO cap. Reads the LIVE notes buffer, so
 * typing "motivated seller" into notes lights the keyword in real time
 * (v1 only recomputed on save — strengthen-as-you-go). */
import { useState } from "react";
import { fmtMoney, type Lead } from "../../types";
import {
  deBadgeHue, deBom, deCountySoft, deCuts, deDom, deKeywords, deLastCutAt, deScore, deSuggestOffer,
  outcomeFor,
} from "./dealEngine";
import { ScriptNavigator } from "./ScriptNavigator";
import type { Flow } from "./scripts";

const pctFmt = (lo: number, hi: number): string => `${Math.round(lo * 100)}–${Math.round(hi * 100)}% of list`;

export function DealEngineCard({
  lead,
  fields,
  notes,
  now,
  outcomeLog,
  onField,
  myName = "Tom",
  onAutoNumbers,
  autoBusy = false,
  autoNote = null,
}: {
  lead: Lead;
  fields: Record<string, unknown> | undefined;
  notes: string;
  now: number;
  outcomeLog: unknown;
  onField: (address: string, key: string, value: string | null) => void;
  myName?: string;
  /** v1 deAutoNums — one-click auto-fill of rehab + comped ARV. */
  onAutoNumbers?: () => void;
  autoBusy?: boolean;
  autoNote?: string | null;
}) {
  const [nav, setNav] = useState<{ view: "script"; flow: Flow } | { view: "sheet" } | null>(null);
  const ol = outcomeFor(outcomeLog, lead.address);
  const kw = deKeywords(lead.desc, notes);
  const score = deScore(lead, notes, now, ol);
  const offer = deSuggestOffer(lead, fields, notes, now, ol);
  const dom = deDom(lead, now, ol);
  const cuts = deCuts(lead);
  const freshCut = cuts > 0 && now - deLastCutAt(lead) < 7 * 86400000;
  const soft = deCountySoft(lead.county);
  const bom = deBom(ol);
  const off = lead.marketStatus === "off";

  return (
    <section className="decard">
      <div className="dehead">
        <span className={"descore de-" + deBadgeHue(score)} title="Deal score 0–100: keywords × DOM × price cuts × county">
          {score}
        </span>
        <div className="detitle">
          <strong>Deal Engine</strong>
          <span className="sub">{off ? "Off market — engine idles" : kw.tier ? kw.tier.name : "No distress keywords yet"}</span>
        </div>
      </div>

      {offer ? (
        <div className="deoffer">
          <div className="deband">
            <span className="lbl">Suggested offer</span>
            <strong>
              {offer.lo === offer.hi ? fmtMoney(offer.lo) : `${fmtMoney(offer.lo)} – ${fmtMoney(offer.hi)}`}
            </strong>
          </div>
          <span className="depct">
            {pctFmt(offer.pctLo, offer.pctHi)}
            {offer.capped ? " · capped at MAO" : ""}
          </span>
        </div>
      ) : (
        <div className="deoffer">
          <span className="depct">No list price on this lead — no % band to price from.</span>
        </div>
      )}

      <div className="demao">
        {offer && offer.mao !== null ? (
          <>MAO <strong>{fmtMoney(offer.mao)}</strong> <span className="sub">(72% of ARV − rehab)</span></>
        ) : (
          <span className="sub">Add a Listed or Comped ARV above to unlock the MAO cap.</span>
        )}
      </div>

      <div className="designals">
        <span>DOM <strong>{dom}d</strong></span>
        <span>{cuts} price cut{cuts === 1 ? "" : "s"}{freshCut ? " · cut this week" : ""}</span>
        {soft ? <span className="desoft">soft county</span> : null}
        {bom ? <span className="debom" title="Fell out of pending — highest-motivation moment">🔁 back on market</span> : null}
      </div>

      {kw.kws.length ? (
        <div className="dekws">
          {kw.kws.slice(0, 8).map((w) => (
            <span key={w} className="dekw">{w}</span>
          ))}
        </div>
      ) : null}

      <div className="descripts">
        <button className="btn slim" onClick={() => setNav({ view: "script", flow: "text" })}>💬 Text script</button>
        <button className="btn slim" onClick={() => setNav({ view: "script", flow: "call" })}>📞 Call script</button>
        <button className="btn slim" onClick={() => setNav({ view: "sheet" })}>📄 Offer sheet</button>
        {onAutoNumbers ? (
          <button className="btn slim deauto" disabled={autoBusy} onClick={onAutoNumbers} title="Auto-fill a rehab estimate and pull a comped ARV">
            {autoBusy ? "⚡ Running…" : "⚡ Auto numbers"}
          </button>
        ) : null}
      </div>
      {autoNote ? <div className="deautonote">{autoNote}</div> : null}

      {nav ? (
        <ScriptNavigator
          lead={lead} fields={fields} notes={notes} now={now} outcomeLog={outcomeLog} myName={myName}
          initial={nav} onField={onField} onClose={() => setNav(null)}
        />
      ) : null}
    </section>
  );
}
